import React, { useEffect, useState, useContext, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./ProductCard.css";
import { FaHeart, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { addToWishlist, removeFromWishlist } from "../wishlist/Wishlist";
import { addToCart } from "../cart/Cart";

const ProductCard = () => {
  const [addingId, setAddingId] = useState(null);

  const [cartQtyMap, setCartQtyMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { setCartCount } = useContext(AuthContext);

  // Refs for slider
  const sliderRefs = useRef({});

  // Track scroll positions for each category
  const [scrollStates, setScrollStates] = useState({});
  const [hasScrolled, setHasScrolled] = useState({}); // NEW: Track if user has scrolled

  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { getCustomerData, isAuthenticated } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  const handleAddToCart = async (e, product_id) => {
    e.stopPropagation();

    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to add items to cart");
      return;
    }

    if (addingId === product_id) return;

    try {
      setAddingId(product_id);

      // 🔑 get existing qty or default to 0
      const currentQty = cartQtyMap[product_id] || 0;
      const newQty = currentQty + 1;

      // ✅ FIX: Always pass 1 as incrementBy (not newQty)
      // This ensures cart badge increments by 1 per click
      const res = await addToCart(
        customer_id,
        product_id,
        newQty, // Send total quantity to API
        setCartCount,
        1, // ✅ Always increment badge by 1
      );

      if (res.api_success) {
        // ✅ update local qty map
        setCartQtyMap((prev) => ({
          ...prev,
          [product_id]: newQty,
        }));

        toast.success(
          currentQty === 0 ? "Added to cart" : "Quantity increased",
        );
      } else {
        toast.error(res.message || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => setAddingId(null), 500);
    }
  };

  /* =========================
     FETCH PRODUCTS
  ========================== */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoryRes = await axios.post(`${BASE_URL}product_categories`);
        const productRes = await axios.post(`${BASE_URL}products`);

        if (categoryRes.data.api_success) {
          setCategories(categoryRes.data.data);
        }

        if (productRes.data.api_success) {
          setProducts(productRes.data.products);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [BASE_URL]);

  /* =========================
     FETCH WISHLIST IDS
  ========================== */
  useEffect(() => {
    if (!customer_id) return;

    const fetchWishlistIds = async () => {
      try {
        const formData = new FormData();
        formData.append("customer_id", customer_id);

        const res = await axios.post(
          `${BASE_URL}customer_wish_list_details`,
          formData,
        );

        if (res.data.api_success) {
          const ids = res.data.wishlist_items.map((item) => item.product_id);
          setWishlistIds(ids);
        }
      } catch (error) {
        console.error("Wishlist fetch error", error);
      }
    };

    fetchWishlistIds();
  }, [customer_id, BASE_URL]);

  /* =========================
     ❤️ TOGGLE WISHLIST
  ========================== */
  const toggleWishlist = async (product_id) => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to use wishlist");
      return;
    }

    const isWishlisted = wishlistIds.includes(product_id);

    if (isWishlisted) {
      await removeFromWishlist(customer_id, product_id);
      setWishlistIds((prev) => prev.filter((id) => id !== product_id));
      toast.info("Removed from wishlist");
    } else {
      await addToWishlist(customer_id, product_id);
      setWishlistIds((prev) => [...prev, product_id]);
      toast.success("Added to wishlist"); 
    }
  };

  /* =========================
     SLIDER NAVIGATION
  ========================== */
  const scroll = (categoryId, direction) => {
    const slider = sliderRefs.current[categoryId];
    if (!slider) return;

    const scrollAmount = 400; // Adjust scroll distance
    const targetScroll =
      direction === "left"
        ? slider.scrollLeft - scrollAmount
        : slider.scrollLeft + scrollAmount;

    slider.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  /* =========================
     CHECK SCROLL POSITION
  ========================== */
  const checkScrollPosition = (categoryId) => {
    const slider = sliderRefs.current[categoryId];
    if (!slider) return;

    const isAtStart = slider.scrollLeft <= 10;
    const isAtEnd =
      slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 10;

    // ✅ Mark that user has scrolled this category
    if (slider.scrollLeft > 0) {
      setHasScrolled((prev) => ({
        ...prev,
        [categoryId]: true,
      }));
    }

    setScrollStates((prev) => ({
      ...prev,
      [categoryId]: {
        showLeftArrow: !isAtStart,
        showRightArrow: !isAtEnd,
      },
    }));
  };

  /* =========================
     INITIALIZE SCROLL LISTENERS
  ========================== */
  useEffect(() => {
    const sliders = sliderRefs.current;

    Object.keys(sliders).forEach((categoryId) => {
      const slider = sliders[categoryId];
      if (slider) {
        // Initial check
        checkScrollPosition(categoryId);

        // Add scroll listener
        const handleScroll = () => checkScrollPosition(categoryId);
        slider.addEventListener("scroll", handleScroll);

        // Cleanup
        return () => slider.removeEventListener("scroll", handleScroll);
      }
    });
  }, [categories, products]);

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading...</p>;
  }

  return (
    <div className="product-grid-container">
      {categories.map((category) => {
        const categoryProducts = products.filter(
          (product) =>
            product.product_category_id === category.product_category_id,
        );

        if (categoryProducts.length === 0) return null;

        const showSlider = categoryProducts.length > 6;
        const categoryScrollState = scrollStates[
          category.product_category_id
        ] || {
          showLeftArrow: false,
          showRightArrow: true,
        };

        // ✅ Only show arrows if user has scrolled this category
        const showArrows = hasScrolled[category.product_category_id];

        return (
          <div
            key={category.product_category_id}
            className="category-section"
            style={{ marginBottom: "40px" }}
          >
            <div className="category-header">
              <h2 className="category-heading">
                {category.product_category_name}
              </h2>
              <button
                className="view-all-btn"
                onClick={() =>
                  navigate(`/view-all/${category.product_category_id}`, {
                    state: { categoryName: category.product_category_name },
                  })
                }
              >
                View All →
              </button>
            </div>

            <div className="slider-wrapper">
              {/* Left Arrow - Only show if user has scrolled */}
              {showSlider &&
                showArrows &&
                categoryScrollState.showLeftArrow && (
                  <button
                    className="slider-arrow slider-arrow-left"
                    onClick={() => scroll(category.product_category_id, "left")}
                    aria-label="Scroll left"
                  >
                    <FaChevronLeft />
                  </button>
                )}

              {/* Product Grid/Slider */}
              <div
                className={`product-grid ${showSlider ? "slider-mode" : ""}`}
                ref={(el) =>
                  (sliderRefs.current[category.product_category_id] = el)
                }
              >
                {categoryProducts.map((item) => {
                  const isWishlisted = wishlistIds.includes(item.id);

                  return (
                    <div
                      className="product-card"
                      key={item.id}
                      onClick={() => navigate(`/product-details/${item.id}`)}
                    >
                      <div className="product-image-wrapper">
                        {/* ❤️ HEART ICON */}
                        <button
                          className={`favorite-btn ${
                            isWishlisted ? "active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWishlist(item.id);
                          }}
                        >
                          <FaHeart />
                        </button>

                        <img
                          src={item.images?.[0] || "/placeholder.png"}
                          alt={item.product_name}
                          className="product-image"
                        />
                      </div>

                      <div className="product-info">
                        <h3 className="product-name">{item.product_name}</h3>

                        <div className="product-footer">
                          <div className="product-price-wrapper">
                            {item.cost_price && (
                              <span className="product-cost-price">
                                ₹{item.cost_price}
                              </span>
                            )}

                            {item.selling_price && (
                              <span className="product-selling-price">
                                ₹{item.selling_price}
                              </span>
                            )}
                          </div>

                          <button
                            className="add-btn"
                            onClick={(e) => handleAddToCart(e, item.id)}
                            disabled={addingId === item.id}
                            style={{
                              opacity: addingId === item.id ? 0.6 : 1,
                              cursor:
                                addingId === item.id
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            {addingId === item.id ? "..." : "+"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Arrow - Only show if user has scrolled */}
              {showSlider &&
                showArrows &&
                categoryScrollState.showRightArrow && (
                  <button
                    className="slider-arrow slider-arrow-right"
                    onClick={() =>
                      scroll(category.product_category_id, "right")
                    }
                    aria-label="Scroll right"
                  >
                    <FaChevronRight />
                  </button>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductCard;
